import { resolveVerticalStack, type Rect } from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { resolveOriginX } from "./directionalUi.js";

/** 융합 슬롯 한 칸 크기(px) — 합성 슬롯과 동일(merge 탭뷰 따름). */
export const FUSION_SLOT_SIZE = 56;
/** 슬롯 칸 사이 간격(px). */
const SLOT_GAP = 22;
/** 콘텐츠 최상단 팁 캐러셀 영역 높이(px) — 제목·프롬프트 행 제거(하단 [합성]/[융합] 서브탭 라벨과 중복) 후 팁이 최상단. 합성/융합은 슬롯·결과·버튼·정렬행이 많아 26으로 축소. */
const TIP_H = 26;
const RESULT_H = 20;
const BUTTON_H = 30;

export interface ModuleFusionLayout {
  /** 콘텐츠 최상단 팁 캐러셀 영역 (전체폭 컨테이너) — TipCarouselView 배치 기준(제목·프롬프트 행 제거 후 최상단). */
  readonly tip: Rect;
  /** 텍스트 origin x — LTR 0(좌)/RTL 1(우). 슬롯/버튼 미러 보조. */
  readonly textOriginX: number;
  /** 슬롯 2칸이 놓이는 행 영역. */
  readonly slotsRow: Rect;
  /** 슬롯 2칸 개별 영역 (가로 중앙 배치, RTL이면 순서 미러). */
  readonly slotCells: readonly Rect[];
  readonly result: Rect;
  readonly fuseButton: Rect;
  /** 정렬 필터 전용 행(버튼↔리스트 사이) — 텍스트 없는 전용 영역이라 필터버튼이 어떤 텍스트와도 안 겹친다(정적검증). */
  readonly sortRow: Rect;
  readonly list: Rect;
}

/**
 * 융합 탭 세로 스택 레이아웃 (Phaser 비의존 순수). `computeModuleMergeLayout`과 동형. 융합 규칙 설명은
 * 제목 아래 팁 캐러셀(TipCarouselView)로, 융합 산출물 제외 주의는 그 팁 항목(tip.moduleFusion.exclude)으로 이관했다.
 * title→tip(캐러셀)→prompt(재료 선택 1줄)→슬롯행(2)→결과→버튼→리스트를 위에서 아래로 누적 배치해 형제 영역 겹침을 구조적으로 차단한다.
 * 비용(광고/다이아)은 버튼 라벨에 표기(별도 행 없음 — 합성 탭뷰 버튼 규칙 따름).
 * RTL이면 슬롯 순서(첫 재료=슬롯0)를 우측부터 + title/prompt 텍스트를 우측 정렬. LTR 값은 회귀 0.
 */
export function computeModuleFusionLayout(
  content: Rect,
  slotCount: number,
  dir: TextDirection = "ltr",
): ModuleFusionLayout {
  const s = resolveVerticalStack(
    {
      children: [
        { id: "tip", height: TIP_H, gapTop: 0 },
        { id: "slotsRow", height: FUSION_SLOT_SIZE, gapTop: 12 },
        { id: "result", height: RESULT_H, gapTop: 12 },
        { id: "fuseButton", height: BUTTON_H, gapTop: 8 },
        { id: "sortRow", height: 14, gapTop: 8 },
        { id: "list", fill: true, gapTop: 4 },
      ],
    },
    content,
  );
  const rtl = dir === "rtl";
  const centerX = content.x + content.width / 2;
  const step = FUSION_SLOT_SIZE + SLOT_GAP;
  const slotCells: Rect[] = [];
  for (let i = 0; i < slotCount; i++) {
    const slot = rtl ? slotCount - 1 - i : i;
    const cx = centerX + (slot - (slotCount - 1) / 2) * step;
    slotCells.push({
      x: cx - FUSION_SLOT_SIZE / 2,
      y: s.slotsRow!.y,
      width: FUSION_SLOT_SIZE,
      height: FUSION_SLOT_SIZE,
    });
  }
  return {
    tip: { x: content.x, y: s.tip!.y, width: content.width, height: s.tip!.height },
    textOriginX: resolveOriginX(dir),
    slotsRow: s.slotsRow!,
    slotCells,
    result: s.result!,
    fuseButton: s.fuseButton!,
    sortRow: s.sortRow!,
    list: s.list!,
  };
}
